import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  usePlmEventByIdQuery,
  usePlmEventEventsByIdsSubscription,
} from '../../../../types/generated-types';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { PlmEvent } from './types';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { PlmEventDetailViewer } from './plmEvent-detail-view';
import { PlmEventDetailForm } from './plmEvent-detail-form';
import { PlmEventResponseDetailViewer } from './plmEvent-response-tab';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { BottomMenuItems } from '../../../system/services/injectableComponentsManager';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`plm-event-tabpanel-${index}`}
      aria-labelledby={`plm-event-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const PlmEventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setTitle, setTitlePath, setContextMenuItems } =
    useInjectableComponents();
  const [isEditing, setIsEditing] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const { data, loading, error } = usePlmEventByIdQuery({
    variables: { _id: id! },
    skip: !id,
  });

  // Subscribe to real-time updates
  usePlmEventEventsByIdsSubscription({
    variables: { _ids: [id!] },
    skip: !id,
  });

  const isSmall = useIsSmallScreen();

  // Set title and breadcrumb
  React.useEffect(() => {
    const title = isEditing
      ? isSmall
        ? 'Edit PLM Event'
        : 'Edit Peak Load Management Event'
      : isSmall
        ? 'View PLM Event'
        : 'View Peak Load Management Event';

    const titlePath = '/plm-events';

    setTitle(title);
    setTitlePath(titlePath);
  }, [isEditing, setTitle, setTitlePath, isSmall]);

  const plmEvent: PlmEvent = data?.plmEventById;

  // Set context menu items
  useEffect(() => {
    if (!isEditing && plmEvent) {
      const menuItems: BottomMenuItems = [
        {
          items: [
            {
              id: 'edit-plm-event',
              label: 'Edit this event',
              icon: <EditIcon fontSize="small" />,
              action: () => setIsEditing(true),
              permit: {
                action: 'update',
                subject: 'PlmEvent',
              },
            },
          ],
        },
      ];

      setContextMenuItems(menuItems);
    } else {
      setContextMenuItems(undefined);
    }

    return () => {
      setContextMenuItems(undefined);
    };
  }, [isEditing, plmEvent, setContextMenuItems, isSmall, can]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Typography variant="h4" color="secondary">
          Loading your data..
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          Error loading PLM event: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!plmEvent) {
    return (
      <Box p={3}>
        <Typography>PLM event not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Tooltip title="Back to List">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/plm-events')}
          >
            Back to PLM Events
          </Button>
        </Tooltip>
        {can('update', 'PlmEventResponse') && (
          <Tooltip title="Edit">
            <IconButton
              onClick={() => navigate(`/plm-events/${id}/edit`)}
              sx={{ ml: 2 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {isEditing ? (
        <PlmEventDetailForm
          plmEvent={plmEvent as any}
          onCancel={() => setIsEditing(false)}
          onSuccess={() => setIsEditing(false)}
        />
      ) : (
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="secondary"
              textColor="secondary"
              sx={{
                '& .MuiTab-root:hover': {
                  borderBottom: '2px solid lightblue',
                  paddingBottom: '10px',
                },
              }}
            >
              <Tab label="Event Details" />
              <Tab label="Response" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <PlmEventDetailViewer plmEvent={plmEvent} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <PlmEventResponseDetailViewer plmEvent={plmEvent} />
          </TabPanel>
        </Box>
      )}
    </Box>
  );
};
